import { redirect, useLoaderData } from "react-router-dom";

import { get } from "@/api";
import { Models } from "@/api/types";
import { CURRENT_ORDER_QUERY_KEY } from "@/modules/cart/useCartDetails";
import { GiftCardCartContainer } from "@/modules/giftCards";
import { waitObj } from "@/toolbox/Promise";

import { queryClient } from "../../Root";

async function loader() {
  let platform;
  try {
    platform = await queryClient.fetchQuery(["platform"], () =>
      get("/platform")
    );
  } catch (error) {
    console.error("Error fetching platform data:", error);
    throw redirect("/");
  }

  if (!platform?.platformSettings?.giftCardEnabled) {
    throw redirect("/");
  }

  const membershipDiscount = queryClient.fetchQuery({
    queryKey: ["membership/details"],
    queryFn: () => get("/orders/current/membership/details"),
  });
  const cart = await queryClient.ensureQueryData({
    queryKey: CURRENT_ORDER_QUERY_KEY,
    queryFn: () => get("/orders/current/details").catch(() => null),
  });

  const lineItems = queryClient.fetchQuery({
    queryKey: ["/orders/current/lineitems"],
    queryFn: () => get("/orders/current/lineitems"),
  });

  const locations = queryClient.fetchQuery({
    queryKey: ["platform/locations"],
    queryFn: async () => {
      const response = await get("/platform/locations", {});
      return response.items as Models<"GetPlatformLocationsResult">[];
    },
  });

  const listings = queryClient.fetchQuery(["/listings"], () =>
    get("/listings")
  );

  const giftCards = await queryClient.fetchQuery(["/gift-cards"], () =>
    get("/gift-cards", {})
  );

  const transfers = await queryClient.fetchQuery(
    ["/gift-cards/transfers/by-order/{orderId}"],
    () =>
      get("/gift-cards/transfers/by-order/{orderId}", {
        orderId: cart?.shopDisplayOrderId ?? "",
      })
  );

  return waitObj({
    platform,
    membershipDiscount,
    cart,
    lineItems,
    listings,
    locations,
    giftCards,
    transfers,
  });
}
type LoaderData = Awaited<ReturnType<typeof loader>>;

export const giftCardCartLoader = async () => {
  const data = await loader();

  if (!data.cart) {
    return redirect("/gift-cards/purchase");
  }

  return data;
};

const GiftCardCartPage = () => {
  const {
    platform,
    membershipDiscount,
    locations,
    cart,
    lineItems,
    listings,
    giftCards,
    transfers,
  } = useLoaderData() as LoaderData;

  return (
    <div className="PurchaseGiftCardPage">
      <GiftCardCartContainer
        giftCards={giftCards as Models<"DisplayGiftCardListing">[]}
        platform={platform}
        salesAssociates={[]}
        showNavbar={false}
        membershipDiscount={membershipDiscount}
        cart={cart as Models<"ShopDisplayOrderResult">}
        initialLineItems={lineItems}
        listings={listings}
        initialLocations={locations}
        transfers={transfers}
      />
    </div>
  );
};

export default GiftCardCartPage;
